import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import * as docx from "docx-preview"

import { useDocumentPreviewSlowMs } from "@/features/chat/hooks/use-document-preview-slow-ms"
import { useFileContent } from "@/hooks/use-file-content"
import { logger } from "@/lib/logger"
import {
  clearHighlights,
  highlightTextInContainer,
  scrollCitationHighlightIntoView,
} from "@/utils/dom-highlight"

import styles from "../styles/file-viewer.module.css"

/** CSS points to CSS pixels (1pt = 96/72 px at standard DPI). */
const PT_TO_PX = 96 / 72

/** Default A4 page width in pt (210mm). */
const A4_WIDTH_PT = 595.3

/** Default A4 page margins in pt (~1 inch / 72pt each side). */
const A4_MARGIN_PT = 72

/** Extra horizontal space (px) reserved on each side for the page shadow. */
const SHADOW_MARGIN = 8

export type DocxPreviewViewerProps = {
  fileUrl: string
  highlightQuote?: string | null
  highlightChunk?: string | null
  highlightSnippet?: string | null
  highlightScrollContainer?: HTMLElement | null
  onError?: () => void
  /** Called when the render completes slower than the heavy-preview threshold. */
  onSlowRender?: () => void
}

/**
 * Renders DOCX files using the docx-preview library at full A4 fidelity,
 * then uniformly scales the result to fit the available panel width.
 *
 * docx-preview generates:
 *   <div class="docx-wrapper">        ← grey bg, padding: 30px
 *     <section class="docx">          ← white page, width in pt (e.g. "595.3pt")
 *
 * We strip the wrapper chrome (padding/bg) and scale the section to fit.
 */
export function DocxPreviewViewer({
  fileUrl,
  highlightQuote = null,
  highlightChunk = null,
  highlightSnippet = null,
  highlightScrollContainer = null,
  onError,
  onSlowRender,
}: DocxPreviewViewerProps) {
  const slowThresholdMs = useDocumentPreviewSlowMs()

  // Use refs for slow-render detection so the expensive parse effect
  // doesn't re-run when the config query resolves or callbacks change.
  const slowThresholdRef = useRef(slowThresholdMs)
  slowThresholdRef.current = slowThresholdMs
  const onSlowRenderRef = useRef(onSlowRender)
  onSlowRenderRef.current = onSlowRender

  const [error, setError] = useState<string | null>(null)
  const [renderComplete, setRenderComplete] = useState(false)
  const [scale, setScale] = useState(1)

  const wrapperRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)

  const { data: buffer, isError, isLoading } = useFileContent(fileUrl)

  const highlightSnippets = useMemo(
    () =>
      [highlightQuote, highlightChunk, highlightSnippet].filter(
        (snippet): snippet is string => typeof snippet === "string" && snippet.trim().length > 0
      ),
    [highlightQuote, highlightChunk, highlightSnippet]
  )

  /**
   * Scale the rendered page to fit the panel width.
   * If docx-preview set an explicit page width (in pt), use that.
   * Otherwise apply default A4 dimensions so documents without
   * page metadata still render with proper width and margins.
   */
  const updateScale = useCallback(() => {
    const wrapper = wrapperRef.current
    const inner = innerRef.current
    if (!wrapper || !inner) return

    const section = inner.querySelector("section") as HTMLElement | null
    if (!section) return

    const rawWidth = section.style.width
    const ptValue = parseFloat(rawWidth)
    let pageWidthPt = A4_WIDTH_PT

    if (ptValue > 0 && rawWidth.includes("pt")) {
      pageWidthPt = ptValue
    } else {
      // No page size metadata — apply A4 dimensions so the document
      // looks like it does in Word (proper width + margins).
      section.style.width = `${A4_WIDTH_PT}pt`
      section.style.paddingLeft = `${A4_MARGIN_PT}pt`
      section.style.paddingRight = `${A4_MARGIN_PT}pt`
      section.style.paddingTop = `${A4_MARGIN_PT}pt`
      section.style.paddingBottom = `${A4_MARGIN_PT}pt`
      section.style.boxSizing = "border-box"
    }

    const pageWidthPx = pageWidthPt * PT_TO_PX
    // clientWidth includes padding; subtract it to get the content box width
    // which is what the inner div's percentage width is relative to.
    const availableWidth = wrapper.clientWidth - SHADOW_MARGIN * 2

    if (availableWidth > 0 && pageWidthPx > 0) {
      setScale(Math.min(1, availableWidth / pageWidthPx))
    }
  }, [])

  // Render docx-preview into the inner container.
  useEffect(() => {
    if (!buffer || !innerRef.current) return

    const container = innerRef.current
    container.innerHTML = ""
    setError(null)
    setRenderComplete(false)
    setScale(1)

    const docData = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    })

    const renderStart = performance.now()

    docx
      .renderAsync(docData, container, undefined, {
        className: "docx-preview",
        inWrapper: true,
        breakPages: true,
        ignoreWidth: false,
        ignoreHeight: false,
        ignoreFonts: false,
        renderHeaders: true,
        renderFooters: false,
        renderFootnotes: true,
        renderEndnotes: true,
      })
      .then(() => {
        updateScale()
        setRenderComplete(true)

        const elapsed = performance.now() - renderStart
        if (elapsed > slowThresholdRef.current) {
          logger.info("docx-preview slow render detected", { elapsed: Math.round(elapsed) })
          onSlowRenderRef.current?.()
        }
      })
      .catch((err) => {
        const errMsg = err instanceof Error ? err.message : String(err)
        logger.warn("docx-preview render failed", { message: errMsg })
        setError("Failed to render document. Please try again later.")
        setRenderComplete(false)
        onError?.()
      })

    return () => {
      container.innerHTML = ""
      setRenderComplete(false)
    }
  }, [buffer, onError, updateScale])

  // Re-scale when the panel resizes.
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper || !renderComplete) return

    const observer = new ResizeObserver(() => updateScale())
    observer.observe(wrapper)
    return () => observer.disconnect()
  }, [renderComplete, updateScale])

  // Apply citation highlight after render completes.
  useEffect(() => {
    const container = innerRef.current
    if (!container || !renderComplete) {
      return
    }
    let cancelScroll = () => {}
    let cancelDelayedScroll = () => {}
    let delayedScrollTimer: number | null = null
    clearHighlights(container)

    if (highlightSnippets.length === 0) {
      return
    }

    let highlighted = false
    let highlightQuality: string | null = null
    for (const snippet of highlightSnippets) {
      const result = highlightTextInContainer(container, snippet)
      if (result.found) {
        highlighted = true
        highlightQuality = result.quality
        break
      }
      clearHighlights(container)
    }

    if (highlighted) {
      const mark = container.querySelector("mark.citation-highlight")
      if (mark instanceof HTMLElement) {
        cancelScroll = scrollCitationHighlightIntoView(mark, {
          preferredContainer: highlightScrollContainer ?? wrapperRef.current,
          maxPasses: 3,
        })
        delayedScrollTimer = window.setTimeout(() => {
          cancelDelayedScroll = scrollCitationHighlightIntoView(mark, {
            preferredContainer: highlightScrollContainer ?? wrapperRef.current,
            maxPasses: 2,
          })
        }, 180)
      }
      if (highlightQuality && highlightQuality !== "exact") {
        logger.debug("docx-preview citation highlight: non-exact match", {
          quality: highlightQuality,
        })
      }
    }
    return () => {
      cancelScroll()
      cancelDelayedScroll()
      if (delayedScrollTimer !== null) {
        window.clearTimeout(delayedScrollTimer)
      }
      if (innerRef.current) clearHighlights(innerRef.current)
    }
  }, [renderComplete, highlightScrollContainer, highlightSnippets])

  if (isError) {
    return (
      <div className={styles.infoContainer}>
        <div className={styles.errorMessage}>Failed to load document.</div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={styles.infoContainer}>
        <div className={styles.infoInnerContainer}>
          <h2 style={{ margin: 0, padding: "0 0.5rem" }}>Loading...</h2>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.infoContainer}>
        <div className={styles.errorMessage}>{error}</div>
      </div>
    )
  }

  return (
    <>
      {/* Strip docx-preview wrapper chrome; it adds grey bg + 30px padding
          that we don't want inside a narrow citation panel. */}
      <style>{`
        .docx-preview-wrapper {
          background: none !important;
          padding: 0 !important;
        }
      `}</style>
      <div
        ref={wrapperRef}
        style={{ width: "100%", overflow: "hidden", padding: `${SHADOW_MARGIN}px` }}
      >
        <div
          ref={innerRef}
          style={{
            transformOrigin: "top left",
            transform: `scale(${scale})`,
            width: `${100 / scale}%`,
          }}
        />
      </div>
    </>
  )
}
